import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Flex,
    useDisclosure,
    Button,
} from '@chakra-ui/react';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import TimeAgo from 'javascript-time-ago';
import th from 'javascript-time-ago/locale/th';

import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import ReactMarkdown from 'react-markdown';

import {
    MainContainer,
    ChatContainer,
    MessageList,
    Message,
    MessageInput,
    Avatar,
    ConversationHeader,
    TypingIndicator,
} from "@chatscope/chat-ui-kit-react";
import { MdLayersClear, MdUploadFile } from 'react-icons/md';
import { API_BASE_URL } from '../../config/apiConfig';
import { removeTrailingNewlines } from '../../util/commonUtils';

TimeAgo.addLocale(th);
const timeAgo = new TimeAgo('th-TH');

function DiagChatModal({ id, isOpenProp, onCloseProp, initialMessages = [], title = 'Chat' }) {
    const MySwal = withReactContent(Swal);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [chatID, setChatID] = useState(id);
    const [messages, setMessages] = useState(initialMessages);
    const [msgInputValue, setMsgInputValue] = useState("");
    const inputRef = useRef(null);
    const msgListRef = useRef(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const apiKey = "PFb6d9uSIuFLaoQ8WPZ3jOvxL4lXKrY8";
    const [autoScrollToBottom, setAutoScrollToBottom] = useState(false);
    const initialInfoRef = useRef(null);

    useEffect(() => {
        if (chatID && messages.length > 0) {
            localStorage.setItem(`diag_chat_${chatID}`, JSON.stringify(messages));
        }
    }, [messages, chatID]);

    useEffect(() => {
        if (id && id !== chatID) {
            setChatID(id);
            checkChatIDExistence(id);
        }
    }, [id]);

    useEffect(() => {
        if (isOpenProp) {
            onOpen();
            checkChatIDExistence(id);
            inputRef.current?.focus();
        }
    }, [isOpenProp]);

    useEffect(() => {
        if (msgListRef && msgListRef.current) {
            msgListRef.current?.scrollToBottom("smooth");
            inputRef.current?.focus();
        }
    }, [messages]);

    const checkChatIDExistence = async (chatID) => {
        try {
            const config = {
                method: 'post',
                url: `${API_BASE_URL}/api/site/diag_chat_id_existence`,
                headers: { 'x-api-key': apiKey },
                data: { id: chatID },
            };

            const response = await axios.request(config);
            if (response.data.has_id) {
                const storedMessages = localStorage.getItem(`diag_chat_${chatID}`);
                if (storedMessages) {
                    setMessages(JSON.parse(storedMessages));
                } else {
                    fetchGreetingMessage();
                }
            } else {
                localStorage.removeItem(`diag_chat_${chatID}`);
                setMessages([]);
                fetchGreetingMessage();
            }
        } catch (error) {
            console.error('Error checking chat ID existence:', error);
        }
    };

    const fetchGreetingMessage = async () => {
        const data = { id: chatID, msg: "สวัสดี" };
        if (initialInfoRef.current) {
            data.initialInfo = initialInfoRef.current;
        }

        const config = {
            method: 'post',
            url: `${API_BASE_URL}/api/site/diag_chat`,
            headers: { 'x-api-key': apiKey },
            data: data,
        };

        try {
            setIsFetching(true);
            setIsInitialized(true);
            const response = await axios.request(config);
            const botMessage = response.data.message;

            setMessages((prevMessages) => [
                ...prevMessages,
                { message: botMessage, direction: 'incoming', sender: 'สุขใจ', sentTime: new Date() },
            ]);
        } catch (error) {
            console.error('Error fetching greeting:', error);
        } finally {
            setIsFetching(false);
        }
    };

    const handleSend = async (message) => {
        setMessages([...messages, { message, direction: 'outgoing', sentTime: new Date() }]);
        setMsgInputValue("");

        try {
            setAutoScrollToBottom(false);
            setIsFetching(true);

            const config = {
                method: 'post',
                url: `${API_BASE_URL}/api/site/diag_chat`,
                headers: { 'x-api-key': apiKey },
                data: {
                    id: chatID,
                    msg: message,
                },
            };

            const response = await axios.request(config);
            const botMessage = response.data.message;

            setMessages((prevMessages) => [
                ...prevMessages,
                { message: botMessage, direction: 'incoming', sender: 'สุขใจ', sentTime: new Date() },
            ]);

            inputRef.current?.focus();
        } catch (error) {
            console.error('Error sending message:', error);
        } finally {
            setIsFetching(false);
            setAutoScrollToBottom(true);
            msgListRef.current?.scrollToBottom("smooth");
        }
    };

    const handleUploadInitialInfo = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.json';

        fileInput.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = async (event) => {
                try {
                    const json = JSON.parse(event.target.result);
                    initialInfoRef.current = json;

                    // Reset session
                    const config = {
                        method: 'post',
                        url: `${API_BASE_URL}/api/site/diag_remove_chat_by_id`,
                        headers: { 'x-api-key': apiKey },
                        data: { id: chatID },
                    };

                    const response = await axios.request(config);
                    if (response.data.status === 'success') {
                        localStorage.removeItem(`diag_chat_${chatID}`);
                        setMessages([]);
                        fetchGreetingMessage();

                        Swal.fire('อัปโหลดสำเร็จ', 'เริ่มการสนทนาใหม่ด้วยข้อมูลผู้ป่วยแล้ว', 'success');
                    } else {
                        throw new Error(response.data.message);
                    }
                } catch (error) {
                    Swal.fire('เกิดข้อผิดพลาด', 'ไม่สามารถอ่านไฟล์ JSON ได้', 'error');
                }
            };
            reader.readAsText(file);
        };

        fileInput.click();
    };

    const handleReset = () => {
        MySwal.fire({
            title: 'คุณแน่ใจหรือไม่?',
            text: "คุณต้องการเริ่มการสนทนาใหม่ทั้งหมดหรือไม่?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'ใช่, เริ่มใหม่!',
            cancelButtonText: 'ยกเลิก',
            reverseButtons: true
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const config = {
                        method: 'post',
                        url: `${API_BASE_URL}/api/site/diag_remove_chat_by_id`, // Update to your API endpoint
                        headers: {
                            'x-api-key': apiKey,
                        },
                        data: {
                            id: chatID
                        },
                    };

                    const response = await axios.request(config);

                    if (response.data.status === 'success') {
                        setMessages([]);
                        localStorage.removeItem(`diag_chat_${chatID}`);
                        initialInfoRef.current = null; // 🟡 Clear uploaded JSON
                        fetchGreetingMessage();
                        inputRef.current?.focus();

                        Swal.fire(
                            'รีเซ็ตแล้ว!',
                            'การสนทนาได้ถูกเริ่มใหม่.',
                            'success'
                        );
                    }
                    else {
                        Swal.fire(
                            'เกิดข้อผิดพลาด!',
                            response.data.message,
                            'error'
                        );
                    }
                } catch (error) {
                    Swal.fire(
                        'เกิดข้อผิดพลาด!',
                        'ไม่สามารถลบการสนทนาได้',
                        'error'
                    );
                    console.error('Error resetting chat session:', error);
                }
            }
        });
    };

    return (
        <Modal isOpen={isOpen} onClose={onCloseProp || onClose} size="3xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader display="flex" justifyContent="space-between" alignItems="center">
                    {title}

                </ModalHeader>
                <ModalCloseButton onClick={() => { onClose(); }} />
                <ModalBody>
                    <Flex bgColor="yellow.100" width={"100%"} height="80vh">
                        <MainContainer style={{ height: '100%', width: '100%' }}>
                            <ChatContainer>
                                <ConversationHeader>
                                    <Avatar name="สุขใจ" src="images/sukjai.png" />
                                    <ConversationHeader.Content info="ผู้ช่วยทันตแพทย์" userName="สุขใจ" />
                                    <ConversationHeader.Actions>
                                        <Button
                                            mr={2}
                                            leftIcon={<MdUploadFile />}
                                            size="sm"
                                            colorScheme="orange"
                                            variant="outline"
                                            onClick={handleUploadInitialInfo}
                                        >
                                            แนบไฟล์ผู้ป่วย
                                        </Button>
                                        <Button
                                            leftIcon={<MdLayersClear />}
                                            variant="outline"
                                            fontFamily="kanit"
                                            colorScheme="blue"
                                            fontWeight="100"
                                            size="sm"
                                            onClick={handleReset}
                                        >
                                            เริ่มใหม่ (reset)
                                        </Button>

                                    </ConversationHeader.Actions>

                                </ConversationHeader>

                                <MessageList ref={msgListRef} typingIndicator={isFetching && <TypingIndicator content="สุขใจกำลังพิมพ์..." />}>
                                    {messages.map((m, i) => (
                                        <Message
                                            key={i}
                                            model={{
                                                direction: m.direction,
                                                sender: m.sender,
                                                sentTime: `${m.sentTime}`,
                                            }}
                                        >
                                            <Message.CustomContent>
                                                <ReactMarkdown>{removeTrailingNewlines(m.message)}</ReactMarkdown>
                                            </Message.CustomContent>
                                        </Message>
                                    ))}
                                </MessageList>
                                <MessageInput
                                    autoFocus={true}
                                    attachButton={false}
                                    disabled={isFetching}
                                    placeholder="พิมพ์ข้อความที่นี้"
                                    onSend={handleSend}
                                    ref={inputRef}
                                />
                            </ChatContainer>
                        </MainContainer>
                    </Flex>
                </ModalBody>
                <ModalFooter />
            </ModalContent>
        </Modal>
    );
}

export default DiagChatModal;